import { ColumnDef, SortingState } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import {
  ISamLanguage,
  ISamMedication,
  ISamMedicationSortBy,
} from "../../../@types/admin/AdminMedication";
import { TableSorted } from "../../../components/TableSorted";
import {
  SAM_MEDICATIONS_QUERY_KEY,
  useSamMedicationSummary,
  useSamMedications,
} from "../../../hooks/queries/admin/samMedications";
import { useDebounce } from "../../../hooks/useDebounce";
import {
  AdminSelectField,
  ErrorMessage,
  FilterGroup,
  FilterLabel,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageInfoTooltip,
  PageTitle,
  PageTitleGroup,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  TruncatedCell,
} from "../shared";
import {
  EditionChip,
  EditionCode,
  SummaryRow,
} from "./AdminMedications.styles";

const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

type IStatusFilter = "" | "active" | "retired";
type IAvailabilityFilter = "" | "patients" | "hospital";

/** Column id → API sort key. Columns missing here are not sortable. */
const SORT_KEYS: Record<string, ISamMedicationSortBy> = {
  cnk: "Cnk",
  name: "Name",
  brandName: "BrandName",
  status: "Active",
};

const DEFAULT_SORTING: SortingState = [{ id: "name", desc: false }];

const LANGUAGE_LABELS: Record<ISamLanguage, string> = {
  Nl: "Dutch",
  Fr: "French",
};

function formatStrength(medication: ISamMedication): string {
  if (medication.strength == null) return "";
  return `${medication.strength.toLocaleString("en-GB", {
    maximumFractionDigits: 4,
  })} ${medication.strengthUnit ?? ""}`.trim();
}

/**
 * SAM medications admin page (EN-5240): read-only browser over the Belgian SAM
 * packs (`public."SamMedications"`) that patients of hospitals on the SAM
 * medication source search. Every pack is listed, including hospital-only ones
 * (never offered to patients) and packs retired by a later release. Search
 * matches brand, generic and ingredient names in both languages, or a CNK.
 *
 * The texts come in Dutch and French; the language select decides which one
 * the name columns show and sort on — patients see the one for their app
 * language (French for French, Dutch otherwise).
 */
export function AdminSamMedications() {
  document.title = "SAM medications - Admin";

  const [searchTerm, setSearchTerm] = useState("");
  const [language, setLanguage] = useState<ISamLanguage>("Nl");
  const [status, setStatus] = useState<IStatusFilter>("");
  const [availability, setAvailability] = useState<IAvailabilityFilter>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);

  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

  const sort = sorting[0] ?? DEFAULT_SORTING[0];
  const sortBy = SORT_KEYS[sort.id] ?? "Name";

  const { medications, totalCount, isFetching, isError } = useSamMedications({
    page,
    pageSize,
    search: debouncedSearch.trim(),
    active: status === "" ? undefined : status === "active",
    hospitalOnly: availability === "" ? undefined : availability === "hospital",
    language,
    sortBy,
    sortDescending: sort.desc,
  });
  const { summary, isError: isSummaryError } = useSamMedicationSummary();

  const resetToFirstPage = () => setPage(1);

  const handleSortingChange = (next: SortingState) => {
    setSorting(next.length > 0 ? next : DEFAULT_SORTING);
    resetToFirstPage();
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pagination = {
    currentPage: page,
    pageSize,
    totalPages,
    totalItems: totalCount,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };

  const isFrench = language === "Fr";

  const columns: ColumnDef<ISamMedication, string>[] = useMemo(
    () => [
      { accessorKey: "cnk", header: "CNK" },
      {
        id: "name",
        header: "Name",
        accessorFn: (medication) =>
          isFrench ? medication.displayNameFr : medication.displayNameNl,
      },
      {
        id: "brandName",
        header: "Brand",
        accessorFn: (medication) => medication.brandName ?? "",
      },
      {
        id: "generic",
        header: "Generic",
        accessorFn: (medication) =>
          (isFrench ? medication.genericNameFr : medication.genericNameNl) ??
          "",
        cell: ({ getValue }) => (
          <TruncatedCell title={getValue()}>{getValue()}</TruncatedCell>
        ),
        enableSorting: false,
      },
      {
        id: "strength",
        header: "Strength",
        accessorFn: formatStrength,
        enableSorting: false,
      },
      {
        id: "form",
        header: "Form",
        accessorFn: (medication) =>
          (isFrench ? medication.formFr : medication.formNl) ?? "",
        enableSorting: false,
      },
      {
        id: "packSize",
        header: "Pack",
        accessorFn: (medication) =>
          medication.packSize != null ? String(medication.packSize) : "",
        enableSorting: false,
      },
      {
        // Hospital-only packs are seeded but never offered in the app.
        id: "availability",
        header: "Patients",
        accessorFn: (medication) =>
          medication.hospitalOnly ? "Hospital only" : "Offered",
        cell: ({ row }) => (
          <StatusBadge
            status={row.original.hospitalOnly ? "inactive" : "active"}
          >
            {row.original.hospitalOnly ? "Hospital only" : "Offered"}
          </StatusBadge>
        ),
        enableSorting: false,
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (medication) => (medication.active ? "Active" : "Retired"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.active ? "active" : "inactive"}>
            {row.original.active ? "Active" : "Retired"}
          </StatusBadge>
        ),
      },
    ],
    [isFrench]
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>SAM medications</PageTitle>
          <PageInfoTooltip title="SAM medications">
            Belgian SAM packs the app&apos;s medication search reads for
            hospitals whose medication source is SAM — not a patient&apos;s own
            medications. Hospital-only packs are listed here but never offered
            to patients; retired packs are kept because patient medications
            still point at them.
          </PageInfoTooltip>
        </PageTitleGroup>

        {summary && (
          <SummaryRow data-testid="sam-medications-summary">
            <EditionChip>
              <EditionCode>Packs</EditionCode>
              {summary.total.toLocaleString("en-GB")}
            </EditionChip>
            <EditionChip>
              <EditionCode>Offered</EditionCode>
              {(summary.active - summary.activeHospitalOnly).toLocaleString(
                "en-GB"
              )}
            </EditionChip>
            <EditionChip>
              <EditionCode>Hospital only</EditionCode>
              {summary.activeHospitalOnly.toLocaleString("en-GB")}
            </EditionChip>
            {summary.inactive > 0 && (
              <EditionChip>
                <EditionCode>Retired</EditionCode>
                {summary.inactive.toLocaleString("en-GB")}
              </EditionChip>
            )}
            <EditionChip>
              <EditionCode>Release</EditionCode>
              {summary.dataVersions.join(", ") || "-"}
            </EditionChip>
          </SummaryRow>
        )}
      </PageHeader>

      {isSummaryError && (
        <ErrorMessage>Failed to load the SAM summary.</ErrorMessage>
      )}

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by brand, generic, ingredient or CNK..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            resetToFirstPage();
          }}
        />

        <FilterGroup>
          <FilterLabel htmlFor="sam-medications-language">Language</FilterLabel>
          <AdminSelectField
            id="sam-medications-language"
            value={language}
            onChange={(e) => {
              setLanguage(e.target.value as ISamLanguage);
              resetToFirstPage();
            }}
          >
            {(Object.keys(LANGUAGE_LABELS) as ISamLanguage[]).map((code) => (
              <option key={code} value={code}>
                {LANGUAGE_LABELS[code]}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="sam-medications-availability">
            Patients
          </FilterLabel>
          <AdminSelectField
            id="sam-medications-availability"
            value={availability}
            onChange={(e) => {
              setAvailability(e.target.value as IAvailabilityFilter);
              resetToFirstPage();
            }}
          >
            <option value="">Offered and hospital only</option>
            <option value="patients">Offered to patients</option>
            <option value="hospital">Hospital only</option>
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="sam-medications-status">Status</FilterLabel>
          <AdminSelectField
            id="sam-medications-status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as IStatusFilter);
              resetToFirstPage();
            }}
          >
            <option value="">Active and retired</option>
            <option value="active">Active</option>
            <option value="retired">Retired</option>
          </AdminSelectField>
        </FilterGroup>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the SAM medications. Adjust the filters and try again.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[[SAM_MEDICATIONS_QUERY_KEY]]}>
          <TableSorted
            columns={columns}
            data={medications}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            manualSorting
            emptyStateText={
              isFetching ? (
                <TableLoadingState />
              ) : (
                "No SAM medications match these filters"
              )
            }
          />
        </TableWrapper>
        <TablePagination
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            resetToFirstPage();
          }}
          pageSizeOptions={[25, 50, 100, 250]}
          exportCsv={{
            filename: "sam-medications",
            columns,
            rows: medications,
            currentPageOnly: true,
          }}
        />
      </TableContainer>
    </PageContainer>
  );
}
